/**
 * Körpergewicht über die Zeit.
 *
 * Ein einzelner Wiegewert schwankt um ein, zwei Kilo (Wasser, Salz, Uhrzeit).
 * Aussagekräftig ist der **Schnitt einer Woche** gegen den der Woche davor —
 * das zeigt die App, nicht die Tageszahl allein. Zu wenige Werte: `null`,
 * keine Behauptung.
 */

import { addDays } from './time.js';

export interface WeighIn {
  /** Kalendertag in Ortszeit, `YYYY-MM-DD`. */
  date: string;
  kg: number;
}

export interface WeightTrend {
  latest: WeighIn | null;
  /** Schnitt der letzten sieben Tage (heute eingeschlossen). */
  avg7: number | null;
  /** Schnitt der sieben Tage davor. */
  avgPrev7: number | null;
  /** `avg7 − avgPrev7` in kg; `null`, wenn eine Woche zu dünn ist. */
  change: number | null;
  /** Wiegetage in den letzten sieben Tagen. */
  count7: number;
}

/** Mindestens so viele Wiegetage je Woche, damit ein Schnitt etwas sagt. */
export const MIN_WEIGH_INS_PER_WEEK = 3;

export function weightTrend(points: readonly WeighIn[], today: string): WeightTrend {
  const sorted = [...points]
    .filter((point) => point.date <= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  const inRange = (from: string, to: string) =>
    sorted.filter((point) => point.date >= from && point.date <= to).map((point) => point.kg);
  const week = inRange(addDays(today, -6), today);
  const before = inRange(addDays(today, -13), addDays(today, -7));
  const avg7 = average(week);
  const avgPrev7 = average(before);
  return {
    latest: sorted.at(-1) ?? null,
    avg7,
    avgPrev7,
    change: avg7 !== null && avgPrev7 !== null ? round1(avg7 - avgPrev7) : null,
    count7: week.length,
  };
}

function average(values: readonly number[]): number | null {
  if (values.length < MIN_WEIGH_INS_PER_WEEK) return null;
  return round1(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// --- Körpermaße (23.10.2026) -------------------------------------------------

/**
 * Umfänge in cm und Körperfett in % — wie die Nährstoffe eine Karte, kein
 * Satz Spalten: Fehlt ein Schlüssel, wurde nicht gemessen (nie 0).
 */
export const MEASUREMENT_KEYS = ['waist', 'hips', 'chest', 'upperArm', 'thigh', 'bodyFat'] as const;
export type MeasurementKey = (typeof MEASUREMENT_KEYS)[number];
export type Measurements = Partial<Record<MeasurementKey, number>>;

/**
 * Grenzen fangen die verrutschte Kommastelle (825 statt 82,5), sie bewerten
 * nichts. Dieselben Grenzen gelten im Formular und an der Sync-Grenze.
 */
export const MEASUREMENT_LIMITS: Record<
  MeasurementKey,
  { min: number; max: number; unit: 'cm' | '%' }
> = {
  waist: { min: 40, max: 250, unit: 'cm' },
  hips: { min: 50, max: 250, unit: 'cm' },
  chest: { min: 50, max: 250, unit: 'cm' },
  upperArm: { min: 15, max: 80, unit: 'cm' },
  thigh: { min: 25, max: 120, unit: 'cm' },
  bodyFat: { min: 2, max: 70, unit: '%' },
};

export function isMeasurementKey(key: string): key is MeasurementKey {
  return (MEASUREMENT_KEYS as readonly string[]).includes(key);
}

/** Liegt der Wert in den Grenzen seines Maßes? */
export function measurementInRange(key: MeasurementKey, value: number): boolean {
  const { min, max } = MEASUREMENT_LIMITS[key];
  return Number.isFinite(value) && value >= min && value <= max;
}

/** Abstand, ab dem ein früherer Wert als Vergleich taugt — Umfänge ändern sich langsam. */
export const MEASUREMENT_COMPARE_DAYS = 28;

export interface MeasurementTrend {
  latest: { date: string; value: number } | null;
  /** Vergleichswert: der letzte mindestens vier Wochen davor, sonst der älteste. */
  previous: { date: string; value: number } | null;
  change: number | null;
}

/**
 * Verlauf eines Maßes. Verglichen wird mit einem Wert von vor mindestens vier
 * Wochen; gibt es keinen, mit dem ältesten — das Datum steht dann dabei, damit
 * „−2 cm“ nicht nach einem Monat aussieht, wenn es eine Woche war.
 */
export function measurementTrend(
  points: readonly { date: string; measurements: Measurements | null | undefined }[],
  key: MeasurementKey,
  today: string,
): MeasurementTrend {
  const values = points
    .filter((point) => point.date <= today)
    .flatMap((point) => {
      const value = point.measurements?.[key];
      return typeof value === 'number' ? [{ date: point.date, value }] : [];
    })
    .sort((a, b) => a.date.localeCompare(b.date));
  const latest = values.at(-1) ?? null;
  if (!latest) return { latest: null, previous: null, change: null };
  const earlier = values.filter((point) => point.date < latest.date);
  const cutoff = addDays(latest.date, -MEASUREMENT_COMPARE_DAYS);
  const previous = earlier.filter((point) => point.date <= cutoff).at(-1) ?? earlier[0] ?? null;
  return {
    latest,
    previous,
    change: previous ? round1(latest.value - previous.value) : null,
  };
}
